import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useGetApi, useIsNewEntry, useIsOnline } from "../../hooks";
import { getYachtId } from "../../utils";
import { getAmbTotalType } from "./CustomInput.utils";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setPrevEntryData } from "src/redux/slices/generatorSlice";
import dayjs from "dayjs";

const isBeforeNoon = (timeString: string): boolean => {
  if (!timeString || !/^\d{2}:\d{2}$/.test(timeString)) {
    return false;
  }
  const hours = parseInt(timeString.split(":")[0], 10);
  return hours < 12;
};

const isDuringVoyage = (
  timeString: string,
  voyageStart: string | null | undefined,
  voyageEnd: string | null | undefined,
  entryDate: string,
): boolean => {
  if (!timeString || !/^\d{2}:\d{2}$/.test(timeString) || !entryDate) {
    return true;
  }

  const parseDateTime = (dateStr: string, timeStr: string): Date => {
    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(":").map(part => parseInt(part, 10));
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const inputDateTime = parseDateTime(entryDate, timeString);

  const extractDateTime = (
    voyageDateTime: string | null | undefined,
  ): Date | null => {
    if (!voyageDateTime || voyageDateTime.trim() === "") return null;
    const parts = voyageDateTime.split(" - ");
    if (parts.length !== 2) return null;

    const dateStr = parts[0].trim();
    const timeStr = parts[1].trim();

    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(":").map(part => parseInt(part, 10));
    date.setHours(hours, minutes, 0, 0);

    return date;
  };

  const startDateTime = extractDateTime(voyageStart);
  const endDateTime = extractDateTime(voyageEnd);

  const hasStartDateTime = !!startDateTime && !isNaN(startDateTime.getTime());
  const hasEndDateTime = !!endDateTime && !isNaN(endDateTime.getTime());

  if (!hasStartDateTime && !hasEndDateTime) {
    return true;
  }

  let isAfterStart = true;
  let isBeforeEnd = true;

  if (hasStartDateTime) {
    isAfterStart = inputDateTime >= startDateTime;
  }

  if (hasEndDateTime) {
    isBeforeEnd = inputDateTime <= endDateTime;
  }

  return isAfterStart && isBeforeEnd;
};

let isFetchingPrevEntryData = false;
let hasFetchedPrevEntryData = false;

interface Props {
  id: string;
  setValue: (id: string, value: any) => void;
  manualEdit: boolean;
  ambVoyageTotals: string | undefined;
  ambSpeedTotals: string | undefined;
  ambSteamingTotals: string | undefined;
}

const useAmbTotals = ({
  id,
  setValue,
  manualEdit,
  ...ambProperties
}: Props) => {
  const type = useMemo(
    () => getAmbTotalType(ambProperties) as keyof typeof ambProperties,
    [ambProperties],
  );
  const isTotalLog = ambProperties[type] === "totalLog";
  const isTotalVoyage = ambProperties[type] === "totalVoyage";
  const isLast24 = ambProperties[type] === "last24";

  if (manualEdit || !(isTotalLog || isLast24 || isTotalVoyage)) {
    return;
  }

  const { lbeidOrPartId, logbookId } = useParams();
  const isOnline = useIsOnline();
  const isNewEntry = useIsNewEntry();
  const prevEntryData = useSelector(
    state => state.generatorSlice.lbentry.prevEntryData,
  );
  const { entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );

  const voyages = useSelector(state => state.generatorSlice.voyages);
  const dispatch = useDispatch();

  const { refetch } = useGetApi({
    request: "prev-entry-data",
    body: {
      params: {
        lbid: logbookId,
        yachtid: getYachtId(),
      },
    },
    fetchOnInit: false,
  });

  const calculateTotals = useCallback(async () => {
    if (!isOnline) return;

    hasFetchedPrevEntryData =
      (prevEntryData && "list" in prevEntryData) || !!prevEntryData?.list;

    if (
      !hasFetchedPrevEntryData &&
      !isFetchingPrevEntryData &&
      (isNewEntry || !lbeidOrPartId || lbeidOrPartId === "null")
    ) {
      isFetchingPrevEntryData = true;
      try {
        const response = (await refetch()) as Record<string, any>;
        const list = response?.list ?? [];
        dispatch(setPrevEntryData({ list }));
        hasFetchedPrevEntryData = true;
      } finally {
        isFetchingPrevEntryData = false;
      }
    }

    let prev = prevEntryData?.list;
    if (!prev) return;
    const { voyageTotals, speedTotals, steamingTotals, previousVoyage } = prev;

    // let currentVoyageStart: string | null = null;
    // let currentVoyageEnd: string | null = null;
    let firstVoyageStart: string | null = null;
    let lastVoyageEnd: string | null = null;

    const voyageIndices = Object.keys(voyages)
      .map(idx => parseInt(idx))
      .filter(idx => voyages[idx]?.start)
      .sort((a, b) => b - a);
    if (
      (voyageIndices.length > 0 && !previousVoyage) ||
      voyageIndices.length > 1
    ) {
      const lastVoyageIndex = voyageIndices[0];
      firstVoyageStart = voyages[voyageIndices[voyageIndices.length - 1]].start;
      lastVoyageEnd = voyages[lastVoyageIndex].end || null;
    } else if (
      previousVoyage &&
      previousVoyage.startDate &&
      previousVoyage.startTime
    ) {
      firstVoyageStart =
        dayjs(previousVoyage.startDate).format("DD MMM YYYY") +
        " - " +
        previousVoyage.startTime;
      lastVoyageEnd = null;
    }

    // if (voyageIndices.length > 0) {
    //   const lastVoyageIndex = voyageIndices[0];
    //   currentVoyageStart = voyages[lastVoyageIndex].start;
    //   currentVoyageEnd = voyages[lastVoyageIndex].end || null;
    // } else if (previousVoyage && previousVoyage.start && !previousVoyage.end) {
    //   currentVoyageStart = previousVoyage.start;
    //   currentVoyageEnd = null;
    // }

    const allRows = Array.from(document.querySelectorAll(".tripLog"))
      .map(el => el.closest("tr"))
      .filter((tr): tr is HTMLTableRowElement => !!tr);

    const rowsWithTime: HTMLTableRowElement[] = [];

    allRows.forEach(row => {
      const timeInput = row.querySelector<HTMLInputElement>(
        'input[data-unit="time"], input[data-unit="24lock"]',
      );
      if (
        !timeInput ||
        timeInput?.dataset?.id?.startsWith("overlay_") ||
        !timeInput.value
      )
        return false;

      let timeValue = timeInput.value;
      if (
        typeof timeValue === "object" &&
        timeValue !== null &&
        "time" in timeValue
      ) {
        timeValue = (timeValue as any).time;
      }

      if (isLast24) {
        if (
          isBeforeNoon(timeValue) &&
          isDuringVoyage(
            timeValue,
            // currentVoyageStart,
            firstVoyageStart,
            // currentVoyageEnd,
            lastVoyageEnd,
            entrydate,
          )
        ) {
          rowsWithTime.push(row);
        }
      } else if (!isTotalLog) {
        if (
          isDuringVoyage(
            timeValue,
            // currentVoyageStart,
            firstVoyageStart,
            // currentVoyageEnd,
            lastVoyageEnd,
            entrydate,
          )
        ) {
          rowsWithTime.push(row);
        }
      } else {
        rowsWithTime.push(row);
      }
    });

    let newValue = 0;

    try {
      if (type === "ambVoyageTotals") {
        const tripLogInputs = rowsWithTime.map(row =>
          row.querySelector<HTMLInputElement>(".tripLog input:not([disabled])"),
        );

        const firstNonEmptyInput = tripLogInputs.find(
          input => input?.value && input.value !== "",
        );

        const lastNonEmptyInput = tripLogInputs
          .filter(input => input?.value && input.value !== "")
          .pop();

        // Calculate the difference for current entry rows
        newValue =
          (parseFloat(lastNonEmptyInput?.value ?? "0") || 0) -
          (!isTotalLog ? parseFloat(firstNonEmptyInput?.value ?? "0") || 0 : 0);

        newValue < 0 && (newValue = 0);

        if (isTotalLog) {
          newValue += voyageTotals?.totalLog ?? 0;
        } else if (isLast24) {
          newValue += voyageTotals?.last24 ?? 0;
        } else if (isTotalVoyage) {
          newValue += voyageTotals?.totalVoyage ?? 0;
        }
      } else if (type === "ambSteamingTotals") {
        const sogInputs = rowsWithTime
          .map(row =>
            row.querySelector<HTMLInputElement>(".SOG input:not([disabled])"),
          )
          .filter(i => i?.value && parseFloat(i.value) > 0);

        const steamingHours = sogInputs.length;
        newValue = steamingHours;

        if (isLast24) {
          newValue += steamingTotals?.last24 ?? 0;
        } else if (isTotalVoyage) {
          newValue += steamingTotals?.totalVoyage ?? 0;
        }
      } else if (type === "ambSpeedTotals") {
        const sogValues = rowsWithTime
          .map(
            row =>
              row.querySelector<HTMLInputElement>(".SOG input:not([disabled])")
                ?.value ?? "",
          )
          .map(v => parseFloat(v) || 0)
          .concat(
            isLast24
              ? (speedTotals?.last24 ?? [])
              : (speedTotals?.totalVoyage ?? []),
          );

        const averageSpeed =
          sogValues.length > 0
            ? sogValues.reduce((a, b) => a + b, 0) / sogValues.length
            : 0;
        newValue = parseFloat(averageSpeed.toFixed(2));
      }
    } catch (err) {
      console.error(err);
    }

    setValue(id, !isNaN(newValue) ? newValue : 0);
  }, [
    id,
    isOnline,
    type,
    refetch,
    setValue,
    isTotalLog,
    isLast24,
    isTotalVoyage,
    prevEntryData,
    voyages,
    entrydate,
    dispatch,
  ]);

  useEffect(() => {
    const calcInputs = Array.from(
      document.querySelectorAll(
        '.tripLog input, .SOG input, input[data-unit="time"], input[data-unit="24lock"]',
      ),
    ).filter(
      i =>
        !(i as HTMLInputElement).disabled &&
        !(i as HTMLInputElement).name.startsWith("overlay_"),
    );
    calcInputs.forEach(i => i.addEventListener("blur", calculateTotals));
    return () => {
      calcInputs.forEach(i => i.removeEventListener("blur", calculateTotals));
    };
  }, [isTotalLog, isLast24, calculateTotals]);

  useEffect(() => {
    if (hasFetchedPrevEntryData && prevEntryData?.list) calculateTotals();
  }, [hasFetchedPrevEntryData]);

  useEffect(() => {
    calculateTotals();
  }, []);
};

export default useAmbTotals;
